import React, { useState, useEffect, useContext } from 'react';
import { Row, Col, Container, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { UserContext } from 'context/userContext';

// api
import { listLanguages } from 'api/libros';
import { getMateriasIbicAll } from 'api/company';

// components
import Select from 'components/userzone/CustomSelectSimple';
import Button from 'components/userzone/buttons/ButtonCustom';
import { modernSelectStyles } from 'utils/selectStyles';
// import './ListItemsFilter.scss';

const ListItemsFilter = (props) => {
  const [languages, setLanguages] = useState([]);
  const [loadingLanguages, setLoadingLanguages] = useState(true);
  const [showAllLanguages, setShowAllLanguages] = useState(false);

  const [materiasIbic, setMateriasIbic] = useState([]);
  const [loadingMateriasIbic, setLoadingMateriasIbic] = useState(true);

  const { handleFilter, cleanSearch, filters } = props;
  const { setSearchData } = useContext(UserContext);

  const { t } = useTranslation(['list', 'libraries', 'backoffice']);

  const options = [
    { value: null, label: t('select') },
    { value: 'title', label: `${t('title')} (${t('sort')} A-Z)` },
    { value: '-title', label: `${t('title')} (${t('sort')} Z-A)` },
    { value: 'price', label: `${t('price')} (${t('sortascendant')})` },
    { value: '-price', label: `${t('price')} (${t('sortdescendant')})` },
    { value: '-date', label: `${t('datepublication')} (${t('sortdescendant')})` },
  ];

  const interestAgeOptions = [
    { value: '0-2', label: t('interestAgeOption0to2') },
    { value: '3-4', label: t('interestAgeOption3to4') },
    { value: '5-6', label: t('interestAgeOption5to6') },
    { value: '7-9', label: t('interestAgeOption7to9') },
    { value: '10-12', label: t('interestAgeOption10to12') },
  ];

  const currentYear = new Date().getFullYear();

  const yearOptions = [
    { value: null, label: t('select') },
    ...Array.from({ length: 30 }, (_, i) => ({
      value: String(currentYear - i),
      label: String(currentYear - i),
    })),
  ];

  const handleCleanSearch = () => {
    cleanSearch();
    setSearchData({
      value: '',
      searchType: '',
    });
  };

  const getLanguages = async () => {
    setLoadingLanguages(true);

    try {
      let { data } = await listLanguages();

      if (data) {
        data = data.map((val) => ({
          id: val.id,
          value: val.nombre,
        }));

        setLanguages(data);
      }
    } catch (error) {
      console.error('Error loading languages:', error);
    } finally {
      setLoadingLanguages(false);
    }
  };

  const getMateriasIbicList = async () => {
    setLoadingMateriasIbic(true);

    try {
      const { data, status } = await getMateriasIbicAll();

      if (data && status === 200) {
        const results = Array.isArray(data)
          ? data
          : data.results || [];

        const materias = results.map((val) => ({
          value: val.id?.toString?.() ?? String(val.id),
          label: val.codigo
            ? `${val.codigo} (${val.descripcion})`
            : val.descripcion,
        }));

        setMateriasIbic(materias);
      } else {
        setMateriasIbic([]);
      }
    } catch (error) {
      console.error('Error loading materias IBIC:', error);
      setMateriasIbic([]);
    } finally {
      setLoadingMateriasIbic(false);
    }
  };

  useEffect(() => {
    getLanguages();
    getMateriasIbicList();
  }, []);

  return (
    <div className="filterCol modern-filter">
      <Container fluid>
        <Row className="pt-2 mx-0">
          
          {/* ORDENAR */}
          <Col lg={12} className="px-0 mb-4">
            <div className="filter-section">
              <h6 className="filter-section-title">
                {t('sortby')}
              </h6>

              <Select
              styles={modernSelectStyles}
                onChange={(event) =>
                  handleFilter('selectFilter', event.value)
                }
                options={options}
                placeholder={t('select')}
                value={options.find(
                  (option) =>
                    option.value === filters.selectFilter
                )}
              />
            </div>
          </Col>

          {/* EDAD */}
          <Col lg={12} className="px-0 mb-4">
            <div className="filter-section">
              <h6 className="filter-section-title">
                {t('interestAge')}
              </h6>

              <div className="filter-chips">
                {interestAgeOptions.map((el) => (
                  <button
                    key={el.value}
                    type="button"
                    className={`filter-chip ${
                      String(filters.edad_interes) === String(el.value)
                        ? 'active'
                        : ''
                    }`}
                    onClick={() =>
                      handleFilter(
                        'edad_interes',
                        filters.edad_interes === el.value
                          ? ''
                          : el.value
                      )
                    }
                  >
                    {el.label}
                  </button>
                ))}
              </div>
            </div>
          </Col>

          {/* AÑO */}
          <Col lg={12} className="px-0 mb-4">
            <div className="filter-section">
              <h6 className="filter-section-title">
                {t('year')}
              </h6>

              <Select
              styles={modernSelectStyles}
                onChange={(event) =>
                  handleFilter(
                    'fecha_public',
                    event.value
                  )
                }
                options={yearOptions}
                placeholder={t('select')}
                value={yearOptions.find(
                  (option) =>
                    option.value === filters.fecha_public
                )}
              />
            </div>
          </Col>

          {/* IBIC */}
          <Col lg={12} className="px-0 mb-4">
            <div className="filter-section">
              <h6 className="filter-section-title">
                {t('ibicSubjects', {
                  ns: 'backoffice',
                })}
              </h6>

              {loadingMateriasIbic ? (
                <div className="d-flex align-items-center">
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="mr-2"
                  />

                  <span>
                    {t('cargando', {
                      ns: 'backoffice',
                    })}
                    ...
                  </span>
                </div>
              ) : (
                <Select
                styles={modernSelectStyles}
                  onChange={(event) =>
                    handleFilter(
                      'typeIbic',
                      event ? event.value : ''
                    )
                  }
                  options={materiasIbic}
                  placeholder={t('select')}
                  value={
                    materiasIbic.find(
                      (option) =>
                        option.value ===
                        (filters.typeIbic ?? '').toString()
                    ) || null
                  }
                  isSearchable
                  isClearable
                />
              )}
            </div>
          </Col>

          {/* IDIOMAS */}
          <Col lg={12} className="px-0 mb-4">
            <div className="filter-section">
              <h6 className="filter-section-title">
                {t('language')}
              </h6>

              {loadingLanguages ? (
                <div className="d-flex align-items-center">
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="mr-2"
                  />

                  <span>
                    {t('cargando', {
                      ns: 'backoffice',
                    })}
                    ...
                  </span>
                </div>
              ) : (
                <>
                  <div className="filter-chips">
                    {(showAllLanguages
                      ? languages
                      : languages.slice(0, 8)
                    ).map((el) => (
                      <button
                        key={el.id}
                        type="button"
                        className={`filter-chip ${
                          String(filters.languageFilter) ===
                          String(el.id)
                            ? 'active'
                            : ''
                        }`}
                        onClick={() =>
                          handleFilter(
                            'languageFilter',
                            filters.languageFilter === el.id
                              ? ''
                              : el.id
                          )
                        }
                      >
                        {el.value}
                      </button>
                    ))}
                  </div>

                  {languages.length > 8 && (
                    <button
                      type="button"
                      className="filter-more-btn"
                      onClick={() =>
                        setShowAllLanguages(
                          !showAllLanguages
                        )
                      }
                    >
                      {showAllLanguages
                        ? t('verMenos')
                        : t('verMas')}
                    </button>
                  )}
                </>
              )}
            </div>
          </Col>

          <Col lg={12} className="px-0">
            <Button
              variant="primary"
              extraClass="mb-0 w-100"
              onClick={handleCleanSearch}
            >
              {t('clearsearch', {
                ns: 'libraries',
              })}
            </Button>
          </Col>

        </Row>
      </Container>
    </div>
  );
};

ListItemsFilter.propTypes = {
  handleFilter: PropTypes.func,
  cleanSearch: PropTypes.func,
  filters: PropTypes.object,
};

export default ListItemsFilter;